import React from "react";
import {GeoLocation, TimeSpan, Utility} from "@renta-apps/athenaeum-toolkit";
import {AlertModel, AlertType, ch, IBaseComponent, TextAlign, VerticalAlign} from "@renta-apps/athenaeum-react-common";
import {
  AddressDivider,
  Alert,
  Button,
  ButtonContainer,
  ButtonType,
  CellModel,
  Checkbox,
  ColumnDefinition,
  ColumnType,
  Dropdown,
  DropdownOrderBy,
  DropdownRequiredType,
  EmailInput,
  Form,
  Grid,
  IconSize,
  Inline,
  JustifyContent,
  List,
  NumberInput,
  OneColumn,
  PageContainer,
  PageHeader,
  PageRow,
  SelectListItem,
  Spinner,
  Tab,
  TabContainer,
  TextInput,
  ThreeColumns,
  ToolbarButton,
  ToolbarContainer,
  ToolbarRow,
  TwoColumns
} from "@renta-apps/athenaeum-react-components";
import PhoneInput from "@/components/PhoneInput/PhoneInput";
import AuthorizedPage from "../../models/base/AuthorizedPage";
import User from "../../models/server/User";
import UserRole from "../../models/server/UserRole";
import SaveUserRequest from "../../models/server/requests/SaveUserRequest";
import OrganizationContract from "../../models/server/OrganizationContract";
import {AuthType, LoginResultStatus, UserRoleGroup} from "@/models/Enums";
import DeleteUserResponse from "@/models/server/responses/DeleteUserResponse";
import UserInvitation from "@/models/server/UserInvitation";
import SaveUserResponse from "@/models/server/responses/SaveUserResponse";
import GetUsersRequest from "@/models/server/requests/GetUsersRequest";
import CostPool from "@/models/server/CostPool";
import Warehouse from "@/models/server/Warehouse";
import AddSubcontractorContractModal from "@/pages/UserManagement/AddSubcontractorContractModal/AddSubcontractorContractModal";
import FeatureFlags from "@/helpers/FeatureFlags";
import AddSubcontractorRequest from "@/models/server/requests/AddSubcontractorRequest";
import AddSubcontractorResponse from "@/models/server/responses/AddSubcontractorResponse";
import GetSubcontractorContractsRequest from "@/models/server/requests/GetSubcontractorContractsRequest";
import TransformProvider from "@/providers/TransformProvider";
import EnumProvider from "@/providers/EnumProvider";
import UnleashHelper from "@/helpers/UnleashHelper";
import Localizer from "../../localization/Localizer";
import {FeatureSwitch} from "@/components/FeatureSwitch/FeatureSwitch";
import LocalizationHelper from "@/helpers/LocalizationHelper";
import Region from "@/models/server/Region";
import Group from "@/models/server/Group";
import DefaultWorkOrderValuesTab from "./DefaultWorkOrderValuesTab/DefaultWorkOrderValuesTab";
import AbsencesManagementTab from "./AbsencesManagementTab/AbsencesManagementTab";
import SaveUserDefaultWorkOrderValuesRequest from "@/models/server/requests/SaveUserDefaultWorkOrderValuesRequest";
import SaveAbsenceRequest from "@/models/server/requests/SaveAbsenceRequest";
import SaveAbsenceResponse from "@/models/server/responses/SaveAbsenceResponse";
import GetUserAbsencesRequest from "@/models/server/requests/GetUserAbsencesRequest";
import UserAbsenceItem from "@/models/server/UserAbsenceItem";
import UserDefaultWorkOrderValues from "@/models/server/UserDefaultWorkOrderValues";
import WorkOrderType from "@/models/server/WorkOrderType";
import ConstructionSiteOrWarehouse from "@/models/server/ConstructionSiteOrWarehouse";

import styles from "../UserManagement/UserManagement.module.scss";

interface IUserManagementProps {
    organizationContracts: OrganizationContract[] | null;
}

interface IUserManagementState {
    filterShowDeleted: boolean;
    filterRoleNames: string[];
    user: User | null;
    prevUser: User | null;
    roles: UserRole[];
    groups: Group[];
    showAddButton: boolean;
    isModified: boolean;
    organizationContracts: OrganizationContract[];
    subcontractorContracts: OrganizationContract[];
    costPools: CostPool[] | null;
    regions: Region[] | null;
    warehouses: Warehouse[] | null;
    workOrderTypes: WorkOrderType[] | null;
    constructionSitesOrWarehouses: ConstructionSiteOrWarehouse[] | null;
    managers: User[] | null;
    defaultWorkOrderValues: UserDefaultWorkOrderValues | null;
    isPerformingAction: boolean;
}

export default class UserManagement extends AuthorizedPage<IUserManagementProps, IUserManagementState> {

    public getTitle(): string {
        return Localizer.topNavAdmin;
    }

    state: IUserManagementState = {
        filterShowDeleted: false,
        filterRoleNames: [],
        user: null,
        prevUser: null,
        roles: [],
        groups: [],
        showAddButton: true,
        isModified: false,
        organizationContracts: [],
        subcontractorContracts: [],
        costPools: null,
        regions: null,
        warehouses: null,
        workOrderTypes: null,
        constructionSitesOrWarehouses: null,
        managers: null,
        defaultWorkOrderValues: null,
        isPerformingAction: false
    };

    private readonly _listRef: React.RefObject<List<User>> = React.createRef();
    private readonly _emailRef: React.RefObject<EmailInput> = React.createRef();
    private _warehousesIdsRef: React.RefObject<Dropdown<Warehouse>> = React.createRef();
    private _regionsIdsRef: React.RefObject<Dropdown<Region>> = React.createRef();
    private _costPoolsIdsRef: React.RefObject<Dropdown<CostPool>> = React.createRef();
    private _groupsIdsRef: React.RefObject<Dropdown<Group>> = React.createRef();
    private readonly _subcontractorsDropdownRef: React.RefObject<Dropdown<OrganizationContract>> = React.createRef();

    private _originalUser: User | null = null;

    private readonly _addSubcontractorContractModalRef: React.RefObject<AddSubcontractorContractModal> = React.createRef();

    private readonly _invitationColumns: ColumnDefinition[] = [
        {
            header: Localizer.userManagementGridCreatedByLanguageItemName,
            accessor: "createdBy",
            minWidth: 100,
            maxWidth: 150,
        },
        {
            header: Localizer.userManagementGridTypeLanguageItemName,
            accessor: "type",
            type: ColumnType.Custom,
            format: "InvitationType",
            minWidth: 90
        },
        {
            header: Localizer.userManagementGridAuthTypeLanguageItemName,
            accessor: "authType",
            type: ColumnType.Custom,
            format: "AuthType",
            minWidth: 90,
        },
        {
            header: Localizer.userManagementGridCreatedAtLanguageItemName,
            accessor: "createdAt",
            format: "D",
            textAlign: TextAlign.Center,
            minWidth: 90,
            verticalAlign: VerticalAlign.Middle
        },
        {
            header: Localizer.userManagementGridExpiresAtLanguageItemName,
            accessor: "validTill",
            format: "D",
            textAlign: TextAlign.Center,
            minWidth: 90,
            init: (cell) => this.initValidTill(cell)
        },
        {
            header: Localizer.userManagementGridProcessedAtLanguageItemName,
            accessor: "processedAt",
            format: "D",
            textAlign: TextAlign.Center,
            minWidth: 90
        },
        {
            header: Localizer.userManagementGridReusableLanguageItemName,
            accessor: (model: UserInvitation) => model.reusable ? "✓" : "",
            textAlign: TextAlign.Center,
            minWidth: 90
        }
    ];

    private initValidTill(cell: CellModel<UserInvitation>): void {
        const model: UserInvitation = cell.row.model;
        const diff: TimeSpan = Utility.diff(model.validTill, Utility.utcNow());
        const expired: boolean = (diff.totalMilliseconds < 0);
        if (expired) {
            cell.className = "danger";
        }
    }

    private async executeActionAsync<T>(action: () => Promise<T>): Promise<T | undefined> {
        if (this.state.isPerformingAction) {
            return;
        }

        await this.setState({ isPerformingAction: true });

        try {
            return await action();
        } finally {
            await this.setState({ isPerformingAction: false });
        }
    }

    private async cancelModifyingAsync(): Promise<void> {
        Utility.copyTo(this._originalUser, this.user);
        await this.setIsModifiedAsync(false);
    }

    private async setSelectedUser(selectedUser: User | null): Promise<void> {
        if (this.user !== selectedUser) {
            if (this.state.showAddButton) {
                await this.setState({user: selectedUser, prevUser: null, showAddButton: true});
            }

            this._originalUser = Utility.clone(selectedUser);
            
            // Fetch default work order values for the selected user
            if (selectedUser) {
                await this.fetchUserDefaultWorkOrderValuesAsync(selectedUser);
            }
        }
    }

    private get userFullName(): string {
        return (this.user) ? TransformProvider.userToString(this.user) : "";
    }

    private async saveAsync(): Promise<void> {
        await this.executeActionAsync(async () => {
            const user: User = this.user!;
            const newUser: boolean = (!user.id);

            const request = new SaveUserRequest();
            request.id = user.id;
            request.authType = user.authType;
            request.roleName = user.role.roleName;
            request.email = user.email;
            request.phone = user.phone;
            request.firstname = user.firstname;
            request.lastName = user.lastName;
            request.middleName = user.middleName;
            request.language = user.language;
            request.location = user.homeLocation;
            request.agreementAccepted = this.user!.agreementAccepted;
            request.registrationAccepted = this.user!.registrationAccepted;
            request.warehouseId = this.user!.warehouseId;
            request.costPoolIds = (this.user!.costPools || []).map(costPool => costPool.id);
            request.groupIds = (this.user!.groups || []).map(group => group.id);
            request.filtersBehavior = user.filtersBehavior;
            request.externalId = user.externalId;
            request.useConstructionSiteCommunicationLanguage = user.useConstructionSiteCommunicationLanguage;
            request.canCommunicateToCustomer = user.canCommunicateToCustomer;
            request.communicationLanguage = user.communicationLanguage ?? LocalizationHelper.defaultLanguage;
            request.taxNumber = user.taxNumber;
            request.contractSalaryHourlyPrice = user.contractSalaryHourlyPrice;
            request.sendWorkTime = user.sendWorkTime;

            if (this.showEmployeeNumber) {
                request.employeeNumber = user.employeeNumber;
            }

            if (this.showRegions){
                request.regionIds = (this.user!.regions || []).map(region => region.id);
            }

            if (this.isSubcontractorRole) {
                request.organizationContractIds = [user.role.organizationContractId];

                if (this.isSubcontractorMounter) {
                    request.canCreateWorkOrder = user.canCreateWorkOrder;
                    request.canCompleteWorkOrder = user.canCompleteWorkOrder;
                    request.canProcessForm = user.canProcessForm;
                }
            } else if (this.isContactPersonRole) {
                request.organizationContractIds = user.roles.map(item => item.organizationContractId);
            }

            const response: SaveUserResponse = await this.postAsync("api/admin/saveUser", request);

            if (response.failed) {
                let message: string;

                if (response.emailExists && response.phoneExists) {
                    message = `${Utility.format(Localizer.genericUserEmailExists, request.email)}\n${Utility.format(Localizer.genericUserPhoneExists, request.phone)}`;
                } else if (response.emailExists) {
                    message = Utility.format(Localizer.genericUserEmailExists, request.email)
                } else {
                    message = Utility.format(Localizer.genericUserPhoneExists, request.phone);
                }

                await this.alertErrorAsync(message, true);
                return;
            }

            if (response.invitationSentFailed) {
                const message: string = Utility.format(Localizer.userManagementAlertErrorPhoneNumberInvalid, this.userFullName);
                await this.alertErrorAsync(message, true);
                return;
            }

            const message: string = Utility.format(Localizer.userManagementAlertMessageAccountSaved, this.userFullName);
            await this.alertMessageAsync(message, true);

            const responseUser: User = response.user!;

            this._originalUser = responseUser!;

            if (newUser) {

                await this.list.reloadAsync();

                await this.setState({user: responseUser, showAddButton: true, isModified: false});

                this.list.scrollToSelected();

            } else {
                await this.setIsModifiedAsync(false);

                await this.list.reRenderAsync();
            }
        });
    }

    private async resetPasswordAsync(): Promise<void> {
        await this.executeActionAsync(async () => {
            const userId: string = this.user!.id;
            await this.postAsync<LoginResultStatus>("api/admin/ResetPassword", userId);
            await this.list.reloadAsync();
        });
    }

    private async setFilterShowDeletedAsync(filterShowDeleted: boolean): Promise<void> {
        await this.setState({filterShowDeleted});
        await this.list.reloadAsync();
    }

    private async setFilterRoleNamesAsync(filterRoles: UserRole[]): Promise<void> {
        const filterRoleNames: string[] = filterRoles.map(item => item.roleName);
        await this.setState({filterRoleNames});
        await this.list.reloadAsync();
    }

    private async addUserAsync(): Promise<void> {
        const prevUser: User | null = this.user;

        const user = new User();
        user.language = Localizer.language;
        user.role = this.state.roles[0];
        user.warehouse = ((this.state.warehouses) && (this.state.warehouses.length > 0)) ? this.state.warehouses[0] : null;
        user.warehouseId = (user.warehouse) ? user.warehouse.id : null;

        await this.setState({user, prevUser, showAddButton: false});

        if (this._emailRef.current) {
            this._emailRef.current.focus();
        }
    }

    private async resendInvitationAsync(): Promise<void> {
        await this.executeActionAsync(async () => {
            const userId: string = this.user!.id;
            await this.postAsync("api/admin/resendInvitation", userId);
            await this.list.reloadAsync();
            await this.reRenderAsync();
        });
    }

    private async setIsModifiedAsync(isModified = true, forceReRender: boolean = false): Promise<void> {
        isModified = isModified && this.state.showAddButton;
        if ((forceReRender) || (isModified !== this.state.isModified)) {
            await this.setState({isModified});
        }
    }

    private get isModified(): boolean {
        return this.state.isModified;
    }

    private async setPhoneAsync(value: string): Promise<void> {
        const user: User = {...this.user!, phone: value};
        await this.setState({user});
        await this.setIsModifiedAsync();
    }

    private async setEmailAsync(value: string): Promise<void> {
        const user: User = {...this.user!, email: value};
        await this.setState({user});
        await this.setIsModifiedAsync();
    }

    private async setAuthTypeAsync(item: SelectListItem, userInteration: boolean): Promise<void> {
        if (userInteration) {
            await this.setIsModifiedAsync();
        }

        const user: User = {...this.user!, authType: parseInt(item.value)};
        await this.setState({user});
        await this.reRenderAsync();
    }

    private async setWarehouseAsync(value: Warehouse, userInteraction: boolean): Promise<void> {
        if (userInteraction) {
            await this.setIsModifiedAsync();
        }

        const user: User = {...this.user!, warehouseId: value.id, warehouse: value};
        await this.setState({user});

        await this.reRenderAsync();
    }

    private async setCostPoolsAsync(sender: Dropdown<CostPool>, userInteraction: boolean): Promise<void> {
        if (userInteraction) {
            await this.setIsModifiedAsync();
        }

        const user: User = {...this.user!, costPools: sender.selectedItems};
        await this.setState({user});

        await this.reRenderAsync();
    }
    private async setRegionsAsync(sender: Dropdown<Region>, userInteraction: boolean): Promise<void> {
        if (userInteraction) {
            await this.setIsModifiedAsync();
        }

        const user: User = {...this.user!, regions: sender.selectedItems};
        await this.setState({user});

        await this.reRenderAsync();
    }

    public get alert(): AlertModel {
        const alertModel = new AlertModel();
        alertModel.message = Utility.format(Localizer.userManagementAlertUserPasswordInfo, this.userFullName);
        alertModel.dismissible = false;
        alertModel.alertType = AlertType.Info;
        return alertModel;
    }

    public async deleteUserAsync(): Promise<void> {
        await this.executeActionAsync(async () => {
            const user: User = this.user!;

            const response: DeleteUserResponse = await this.postAsync("api/admin/deleteUser", user.id);

            const message: string = (response.removedPermanently)
                ? Utility.format(Localizer.userManagementUserDeletedPermanently, this.userFullName)
                : Utility.format(Localizer.get(Localizer.userManagementUserDeleted, this.userFullName));

            await this.alertMessageAsync(message, true);

            if (!this.filterShowDeleted || response.removedPermanently) {
                await this.setState({user: null});
                await this.list.reloadAsync();
                this.list.scrollToSelected();
            } else {
                const updatedUser = {...user, isDeleted: true};
                await this.setState({user: updatedUser});
                await this.list.reRenderAsync();
                await this.reRenderAsync();
            }
        });
    }

    private async restoreUserAsync(): Promise<void> {
        await this.executeActionAsync(async () => {
            const user: User = this.user!;

            await this.postAsync("api/admin/restoreUser", user.id);

            const updatedUser = {...user, isDeleted: false};
            await this.setState({user: updatedUser});

            await this.alertMessageAsync(Localizer.get(Localizer.userManagementUserRestored, user.email), true);

            await this.list.reRenderAsync();
            await this.reRenderAsync();
        });
    }

    private async unlockUserAsync(): Promise<void> {
        await this.executeActionAsync(async () => {
            const user: User = this.user!;

            await this.postAsync("api/admin/unlockUser", user.id);

            const updatedUser = {...user, isLocked: false};
            await this.setState({user: updatedUser});

            const message: string = Utility.format(Localizer.userManagementAlertMessageUnlockUser, this.userFullName);

            await this.alertMessageAsync(message, true);

            await this.list.reRenderAsync();
            await this.reRenderAsync();
        });
    }

    private async fetchUserDefaultWorkOrderValuesAsync(user: User | null): Promise<void> {
        if (user && user.id) {
            const defaultValues: UserDefaultWorkOrderValues = await this.postAsync("api/admin/getUserDefaultWorkOrderValues", user.id);
            await this.setState({ defaultWorkOrderValues: defaultValues });
        } else {
            await this.setState({ defaultWorkOrderValues: null });
        }
    }

    private async saveDefaultWorkOrderValuesAsync(request: SaveUserDefaultWorkOrderValuesRequest): Promise<void> {
        await this.executeActionAsync(async () => {
            await this.postAsync("api/admin/saveUserDefaultWorkOrderValues", request);
            await this.alertMessageAsync(Localizer.userManagementDefaultWorkOrderValuesSaved, true);
        });
    }

    private async saveAbsenceAsync(request: SaveAbsenceRequest): Promise<SaveAbsenceResponse> {
        let response: SaveAbsenceResponse = new SaveAbsenceResponse();
        await this.executeActionAsync(async () => {
            response = await this.postAsync("api/account/saveAbsence", request);
            if (response.success) {
                await this.alertMessageAsync(Localizer.absenceSaved, true);
            } else {
                await this.alertErrorAsync(response.errorMessage || Localizer.absenceSaveFailed, true);
            }
        });
        return response;
    }

    private async loadUserAbsencesAsync(request: GetUserAbsencesRequest): Promise<UserAbsenceItem[]> {
        return await this.postAsync("api/account/getUserAbsences", request);
    }

    private async deleteAbsenceAsync(workOrderId: string): Promise<void> {
        await this.executeActionAsync(async () => {
            await this.postAsync("api/account/deleteAbsence", workOrderId);
            await this.alertMessageAsync(Localizer.absenceDeleted, true);
        });
    }

    private async cancelAddUserAsync() {
        await this.setState({user: this.state.prevUser, prevUser: null, showAddButton: true});
    }

    private get canDelete(): boolean {
        const me: boolean = (ch.getUser().id == this.user!.id);
        return !me;
    }

    private async getUsersAsync(sender: List<User>): Promise<User[]> {
        const request = new GetUsersRequest();
        request.showDeleted = this.filterShowDeleted;
        request.roleNames = this.filterRoleNames;
        return await sender.postAsync("api/admin/getUsers", request);
    }

    private async onFirstNameChangeAsync(item: string) {
        const user = {...this.user!, firstname: item};
        await this.setState({user});
        await this.setIsModifiedAsync();
    }

    private async onLastNameChangeAsync(item: string) {
        const user = {...this.user!, lastName: item};
        await this.setState({user});
        await this.setIsModifiedAsync();
    }

    private async onMiddleNameChangeAsync(item: string) {
        const user = {...this.user!, middleName: item};
        await this.setState({user});
        await this.setIsModifiedAsync();
    }

    private async onCommunicationLanguageChangeAsync(item: number, userInteraction: boolean): Promise<void> {
        if (userInteraction)
        {
            const user = {...this.user!, communicationLanguage: item};
            await this.setState({user});
            await this.setIsModifiedAsync();
        }
    }

    private async onEmployeeNumberChangeAsync(item: string) {
        const user = {...this.user!, employeeNumber: item};
        await this.setState({user});
        await this.setIsModifiedAsync();
    }

    private async onExternalIdChangeAsync(item: string) {
        const user = {...this.user!, externalId: item};
        await this.setState({user});
        await this.setIsModifiedAsync();
    }

    private async onTaxNumberChangeAsync(item: string) {
      const user = {...this.user!, taxNumber: item};
      await this.setState({user});
      await this.setIsModifiedAsync();
    }

    private async onContractSalaryHourlyPriceChangeAsync(item: number) {
      const user = {...this.user!, contractSalaryHourlyPrice: item};
      await this.setState({user});
      await this.setIsModifiedAsync();
    }

    private async onCanCreateWorkOrderChangeAsync(canCreateWorkOrder: boolean): Promise<void> {
        const user = {...this.user!, canCreateWorkOrder};
        await this.setState({user});
        await this.setIsModifiedAsync();
    }

    private async onCanCompleteWorkOrderChangeAsync(canCompleteWorkOrder: boolean): Promise<void> {
        const user = {...this.user!, canCompleteWorkOrder};
        await this.setState({user});
        await this.setIsModifiedAsync();
    }

    private async onCanProcessFormChangeAsync(canProcessForm: boolean): Promise<void> {
        const user = {...this.user!, canProcessForm};
        await this.setState({user});
        await this.setIsModifiedAsync();
    }

    private async onCanCommunicateToCustomerFormChangeAsync(canCommunicateToCustomer: boolean): Promise<void> { 
        const user = {...this.user!, canCommunicateToCustomer};
        await this.setState({user});
        await this.setIsModifiedAsync();
    }

    private async onAddressChangeAsync(item: GeoLocation) {
        const user = {...this.user!, homeLocation: item};
        await this.setState({user});
        await this.setIsModifiedAsync();
    }

    private async onCommunicationLanguageCheckboxChangeAsync(value: boolean) {
        const user = {...this.user!, useConstructionSiteCommunicationLanguage: value};
        await this.setState({user});
        await this.setIsModifiedAsync();
    }

    private async onSendWorkTimeChangeAsync(value: boolean): Promise<void> {
        const user = {...this.user!, sendWorkTime: value};
        await this.setState({user});
        await this.setIsModifiedAsync();
    }

    private async setUserRole(userRole: UserRole, userInteraction: boolean): Promise<void> {
        if (userInteraction) {

            const currentUser: User = this.user!;
            const prevUserRole: UserRole = currentUser.role;

            let newRole: UserRole;
            if (this.isSubcontractorRole) {
                const subcontractorOrganizationId: string = currentUser.role.organizationContractId;
                newRole = {...userRole, organizationContractId: subcontractorOrganizationId};
            } else {
                newRole = userRole;
            }

            let newCostPools: CostPool[];
            if (userRole.group == UserRoleGroup.Admins) {
                newCostPools = this.state.costPools || [];
            } else if (prevUserRole.group == UserRoleGroup.Admins) {
                newCostPools = [];
            } else {
                newCostPools = currentUser.costPools || [];
            }

            const user: User = {...currentUser, role: newRole, costPools: newCostPools};
            await this.setState({user});

            await this.setIsModifiedAsync(true, true);

            await this.reRenderAsync();

            await this.reloadUserCostPoolsIds();
        }
    }

    private get isSubcontractorRole(): boolean {
        return (this.user != null) && (this.user.role.group == UserRoleGroup.Subcontractors);
    }

    private async reloadUserCostPoolsIds(): Promise<void> {
        //Multi-dropdown does not support SelectedListItems, should be reloaded manually
        if (this._costPoolsIdsRef.current) {
            await this._costPoolsIdsRef.current.reRenderAsync();
        }
    }

    private get isContactPersonRole(): boolean {
        return (this.user != null) && (this.user.role.roleName == "ContactPerson");
    }

    private get isSubcontractorMounter(): boolean {
        return (this.user != null) && (this.user.role.roleName == "SubcontractorMounter");
    }

    private get showEmployeeNumber(): boolean {
        return (UnleashHelper.isEnabled(FeatureFlags.EmployeeNumber)) &&
            (this.user != null) &&
            (this.user.role.group === UserRoleGroup.Employees ||
                this.user.role.group === UserRoleGroup.Managers ||
                this.user.role.group === UserRoleGroup.SalesPersons);
    }

    private get showRegions(): boolean {
        return (UnleashHelper.isEnabled(FeatureFlags.SalesPerson)) &&
            (this.user != null) &&
            (this.user.role.group === UserRoleGroup.SalesPersons);
    }

    private get showExternalId(): boolean {
        return (this.user != null) &&
            (this.user.role.group === UserRoleGroup.Managers || this.user.role.group === UserRoleGroup.SalesPersons);
    }

    private async setRoleContractAsync(contract: OrganizationContract, userInteraction: boolean): Promise<void> {
        if ((this.user != null) && (this.user.role.organizationContractId != contract.id)) {
            const updatedRole = {...this.user.role, organizationContractId: contract.id};
            const user = {...this.user, role: updatedRole};
            await this.setState({user});

            if (userInteraction) {
                await this.setIsModifiedAsync();
            }
        }
    }

    private async setRoleContractsAsync(contracts: OrganizationContract[], userInteraction: boolean): Promise<void> {
        if (this.user != null) {
            const roleName: string = this.user.role.roleName;
            const group: UserRoleGroup = this.user.role.group;
            const roles = contracts.map(contract => new UserRole(roleName, contract.id, group));
            const user = {...this.user, roles};
            await this.setState({user});

            if (userInteraction) {
                await this.setIsModifiedAsync();
            }
        }
    }

    private async setGroupsAsync(groups: Group[], userInteraction: boolean): Promise<void> {
        if (this.user != null) {
            const user = {...this.user, groups};
            await this.setState({user});
            if (userInteraction) {
                await this.setIsModifiedAsync();
            }
        }
    }

    private get subcontractorContracts(): OrganizationContract[] {
        return this.state.subcontractorContracts;
    }

    private get organizationContracts(): OrganizationContract[] {
        return this.state.organizationContracts;
    }

    private get user(): User | null {
        return this.state.user;
    }

    private get list(): List<User> {
        return this._listRef.current!;
    }

    private get filterShowDeleted(): boolean {
        return this.state.filterShowDeleted;
    }

    private get filterRoleNames(): string[] {
        return this.state.filterRoleNames;
    }

    private get isEmailRequired(): boolean {
        return ((this.user != null) && ((this.user.authType == AuthType.Email) || (this.user.role.roleName == "ContactPerson")));
    }

    private get isPhoneRequired(): boolean {
        return ((this.user != null) && ((this.user.authType == AuthType.Phone) || (this.user.role.roleName == "ContactPerson")));
    }

    private get multipleCostPools(): boolean {
        return ((this.user != null) && (this.user!.role.group != UserRoleGroup.Admins));
    }

    private get multipleCostPoolsText(): string {
        return (!this.multipleCostPools)
            ? Localizer.userManagementDropdownMultipleCostPoolsText
            : "";
    }

    private async addSubcontractorContract(sender: IBaseComponent, request: AddSubcontractorRequest): Promise<boolean> {
        const response: AddSubcontractorResponse = await sender.postAsync("api/admin/addSubcontractor", request);

        if (response.contractAlreadyExists) {
            return false;
        }

        const message = Localizer.get(Localizer.subcontractorCreatedFlyout, response.organizationContract!.name);

        await ch.flyoutMessageAsync(message);

        const subcontractorContract: OrganizationContract = response.organizationContract!;

        const updatedSubcontractorContracts = [...this.state.subcontractorContracts, subcontractorContract];
        const updatedRole = {...this.user!.role, organizationContractId: subcontractorContract.id};
        const updatedUser = {...this.user!, role: updatedRole};

        await this.setState({
            subcontractorContracts: updatedSubcontractorContracts,
            user: updatedUser
        });

        await this.setIsModifiedAsync(true, true);

        return true;
    }

    override async componentDidMount(): Promise<void> {
        const userId: string | null = this.routeId;
        const isRedirectEnabled: boolean = UnleashHelper.isEnabled(FeatureFlags.CalendarRedirectToUserManagement);

        if (userId && isRedirectEnabled) {
            const getUsersRequest = new GetUsersRequest();
            getUsersRequest.showDeleted = this.filterShowDeleted;
            getUsersRequest.roleNames = this.filterRoleNames;
            const users: User[] = await this.postAsync("api/admin/getUsers", getUsersRequest);

            const user: User | null = users.find(user => user.id === userId) ?? null;

            await this.setState({user});
        }
    }

    public async initializeAsync(): Promise<void> {
        await this.setSpinnerAsync(true);

        // Load only essential data for initial page render
        const roles: UserRole[] = await this.getAsync("api/admin/getUserRoles");

        const request = new GetSubcontractorContractsRequest();
        const subcontractorContracts: OrganizationContract[] = await this.postAsync("api/admin/getSubcontractorContracts", request);

        const organizationContracts: OrganizationContract[] = await this.postCacheAsync("api/admin/getActiveCustomers", 60000);

        const warehouses: Warehouse[] = await this.postAsync("api/admin/getWarehouses");

        const costPools: CostPool[] = await this.postAsync("api/admin/getCostPools");

        const regions: Region[] = await this.getAsync("api/region/getRegions");

        const groups: Group[] = await this.getAsync("api/group/getGroups");

        await this.setState({roles, subcontractorContracts, organizationContracts, costPools, warehouses, regions, groups});
        await this.setSpinnerAsync(false);

        // Load work order related data after initial render to avoid timing issues
        await this.loadWorkOrderDataAsync();
    }

    private async loadWorkOrderDataAsync(): Promise<void> {
        const allWorkOrderTypes: WorkOrderType[] = await this.getAsync("api/workOrderType/getWorkOrderTypes");
        const workOrderTypes: WorkOrderType[] = allWorkOrderTypes.filter(type => !type.disabled);
        const constructionSitesOrWarehouses: ConstructionSiteOrWarehouse[] = await this.postAsync("api/workOrders/getConstructionSitesData", null);
        const managers: User[] = await this.postAsync("api/workOrders/getManagers");

        await this.setState({workOrderTypes, constructionSitesOrWarehouses, managers});
    }

    private async openAddSubcontractorContractModalAsync() {
        await this._addSubcontractorContractModalRef.current!.openAsync();
    }

    public hasSpinner(): boolean {
        return true;
    }

    public render(): React.ReactNode {
        return (
            <PageContainer className={styles.userManagement} fullHeight>
                {
                    (this.isSpinning()) && <Spinner/>
                }

                <PageHeader title={Localizer.userManagementTitle}/>

                <PageRow>
                    <div className="col">

                        <ToolbarContainer>

                            <ToolbarRow justify={JustifyContent.SpaceBetween}>

                                <Form inline className="d-flex">

                                    <Dropdown inline noWrap multiple
                                              id="roleNameFilter"
                                              minWidth={210}
                                              nothingSelectedText={Localizer.userManagementChooseRole}
                                              label={Localizer.formInputRole}
                                              orderBy={DropdownOrderBy.None}
                                              items={this.state.roles}
                                              onChange={async (sender) => await this.setFilterRoleNamesAsync(sender.selectedItems)}
                                    />

                                    <Checkbox inline
                                              id="showDeletedCheckbox"
                                              label={Localizer.userManagementShowDeleted}
                                              value={this.filterShowDeleted}
                                              onChange={async (_, value) => await this.setFilterShowDeletedAsync(value)}
                                    />

                                </Form>

                                <Inline justify={JustifyContent.End}>

                                    <ToolbarButton id="addUserButton"
                                                   label={Localizer.userManagementAddUser}
                                                   icon={{name: "plus", size: IconSize.Large}}
                                                   type={ButtonType.Orange}
                                                   disabled={this.isSpinning() || !this.state.showAddButton || this.isModified}
                                                   onClick={async () => await this.addUserAsync()}
                                    />

                                </Inline>

                            </ToolbarRow>

                        </ToolbarContainer>

                        <div className="row">

                            <div className="col-md-4" style={{ minHeight: 600 }}>

                                <List required noGrouping absoluteListItems
                                      id="users"
                                      ref={this._listRef}
                                      maxHeight={"auto"}
                                      disabled={!this.state.showAddButton || this.isModified}
                                      orderBy={DropdownOrderBy.None}
                                      filterMinLength={10}
                                      fetchItems={async (sender) => await this.getUsersAsync(sender)}
                                      selectedItem={this.user || undefined}
                                      onChange={async (_, item) => await this.setSelectedUser(item)}
                                />

                            </div>

                            <div className="col-md-8">

                                {
                                    (this.user != null) &&
                                    (
                                        <TabContainer id="userManagementTabs">

                                            <Tab id="account" title={Localizer.topNavAccount}>

                                                <Form id="accountForm" onSubmit={async () => await this.saveAsync()}>

                                                    <TwoColumns>
                                                        <Dropdown required noSubtext
                                                                  id="authType"
                                                                  label={Localizer.userManagementLabelAuthenticationType}
                                                                  disabled={!!this.user.id}
                                                                  orderBy={DropdownOrderBy.None}
                                                                  items={EnumProvider.getAuthTypeItems()}
                                                                  selectedItem={EnumProvider.getAuthTypeItem(this.user.authType)}
                                                                  onChange={async (_, item, userInteraction) => await this.setAuthTypeAsync(item!, userInteraction)}
                                                        />

                                                        <Dropdown required
                                                                  id="roleName"
                                                                  nothingSelectedText={Localizer.userManagementChooseRole}
                                                                  label={Localizer.formInputRole}
                                                                  orderBy={DropdownOrderBy.None}
                                                                  value={this.user.role.roleName}
                                                                  items={this.state.roles}
                                                                  selectedItem={this.user.role}
                                                                  onChange={(_, item, userInteraction) => this.setUserRole(item!, userInteraction)}
                                                        />

                                                    </TwoColumns>

                                                    <TwoColumns>
                                                        <Dropdown multiple autoGroupSelected noWrap noSubtext
                                                            required={false}
                                                            id="groupNames"
                                                            ref={this._groupsIdsRef}
                                                            label={Localizer.userManagementLabelUserGroups}
                                                            filterMaxLength={10000000}
                                                            requiredType={DropdownRequiredType.Restricted}
                                                            items={this.state.groups || []}
                                                            selectedItems={this.user.groups || []}
                                                            onChange={async (sender, _, userInteraction) => await this.setGroupsAsync(sender.selectedItems, userInteraction)}
                                                        />

                                                        <div></div>
                                                    </TwoColumns>

                                                    <TwoColumns>

                                                        {
                                                            (this.isContactPersonRole) &&
                                                            (
                                                                <Dropdown required multiple autoGroupSelected noWrap noSubtext
                                                                          id="organizationContractIds"
                                                                          requiredType={DropdownRequiredType.Manual}
                                                                          label={Localizer.userManagementOrganizationContractsLabel}
                                                                          filterMaxLength={10000000}
                                                                          items={this.organizationContracts}
                                                                          selectedItems={this.user.roles.map(item => item.organizationContractId)}
                                                                          onChange={(sender, _, userInteraction) => this.setRoleContractsAsync(sender.selectedItems, userInteraction)}
                                                                />
                                                            )
                                                        }

                                                    </TwoColumns>

                                                    <FeatureSwitch flagName={FeatureFlags.AzureAdSso}>
                                                        {
                                                            (this.user.sso) &&
                                                            <p>{Localizer.userManagementUserSsoInfoLabel}</p>
                                                        }
                                                    </FeatureSwitch>

                                                    <TwoColumns>

                                                        <EmailInput id="email"
                                                                    ref={this._emailRef}
                                                                    label={Localizer.formInputEmail}
                                                                    required={this.isEmailRequired}
                                                                    value={this.user.email}
                                                                    readonly={(this.user.authType == AuthType.Email) && (!!this.user.id)}
                                                                    onChange={async (_, value) => await this.setEmailAsync(value)}
                                                        />

                                                        <PhoneInput id="phone"
                                                                    label={Localizer.formInputPhone}
                                                                    required={this.isPhoneRequired}
                                                                    value={this.user.phone}
                                                                    readonly={(this.user.authType == AuthType.Phone) && (!!this.user.id)}
                                                                    onChange={async (_, value) => await this.setPhoneAsync(value)}
                                                        />

                                                    </TwoColumns>

                                                    {
                                                        (this.isSubcontractorRole) &&
                                                        (
                                                          <>
                                                            <OneColumn className={this.css(styles.subcontractorSection, styles.open)}>
                                                                <TwoColumns>

                                                                    <Dropdown id="subcontractorContracts" ref={this._subcontractorsDropdownRef}
                                                                              required
                                                                              items={this.subcontractorContracts}
                                                                              label={Localizer.userManagementDropdownSubcontractor}
                                                                              selectedItem={this.user.role.organizationContractId}
                                                                              onChange={(_, value, userInteraction) => this.setRoleContractAsync(value!, userInteraction)}
                                                                    />

                                                                    <Button id={"addSubcontractorContract"} icon={{name: "plus", size: IconSize.Large}} className={styles.padding}
                                                                            type={ButtonType.Orange}
                                                                           title={Localizer.userManagementButtonAddNewSubcontractor}
                                                                            label={Localizer.userManagementButtonAddNewSubcontractor}
                                                                            onClick={async () => await this.openAddSubcontractorContractModalAsync()}
                                                                    />

                                                                </TwoColumns>
                                                            </OneColumn>

                                                            <TwoColumns>
                                                                <NumberInput id="contractSalaryHourlyPrice"
                                                                             label={Localizer.subcontractorHourPriceLanguageItemName}
                                                                             step={0.01}
                                                                             format="0.0"
                                                                             min={0}
                                                                             max={99999.99}
                                                                             value={this.user.contractSalaryHourlyPrice}
                                                                             onChange={async (_, item) => await this.onContractSalaryHourlyPriceChangeAsync(item!)}
                                                                />
                                                                <TextInput id="taxNumber"
                                                                         label={Localizer.genericTaxNumberLanguageItemName}
                                                                         value={this.user.taxNumber}
                                                                         onChange={async (_, item) => await this.onTaxNumberChangeAsync(item!)}
                                                                />
                                                            </TwoColumns>
                                                          </>
                                                        )
                                                    }

                                                    {
                                                        (!this.isContactPersonRole) &&
                                                        (
                                                            <TwoColumns>

                                                                <Dropdown required autoGroupSelected noSubtext
                                                                          id="warehousesIds"
                                                                          ref={this._warehousesIdsRef}
                                                                          label={Localizer.genericWarehouse}
                                                                          requiredType={DropdownRequiredType.Restricted}
                                                                          items={this.state.warehouses || []}
                                                                          selectedItem={this.user.warehouse || undefined}
                                                                          onChange={async (_, item, userInteraction) => await this.setWarehouseAsync(item!, userInteraction)}
                                                                />

                                                                <Dropdown noSubtext multiple autoGroupSelected
                                                                          required={!this.isSubcontractorMounter}
                                                                          id="costPoolsIds"
                                                                          ref={this._costPoolsIdsRef}
                                                                          label={Localizer.genericCostPools}
                                                                          requiredType={DropdownRequiredType.Restricted}
                                                                          disabled={!this.multipleCostPools}
                                                                          items={this.state.costPools || []}
                                                                          selectedItems={this.user.costPools || []}
                                                                          selectedTextTransform={() => this.multipleCostPoolsText}
                                                                          onChange={async (sender, _, userInteraction) => await this.setCostPoolsAsync(sender, userInteraction)}
                                                                />

                                                            </TwoColumns>
                                                        )
                                                    }

                                                    {
                                                        (this.showRegions) &&
                                                        (
                                                            <TwoColumns>

                                                                <Dropdown required noSubtext multiple autoGroupSelected
                                                                          id="regionsIds"
                                                                          ref={this._regionsIdsRef}
                                                                          label={Localizer.genericRegion}
                                                                          requiredType={DropdownRequiredType.Restricted}
                                                                          items={this.state.regions || []}
                                                                          selectedItems={this.user.regions || []}
                                                                          onChange={async (sender, _, userInteraction) => await this.setRegionsAsync(sender, userInteraction)}
                                                                />

                                                            </TwoColumns>
                                                        )
                                                    }

                                                    {
                                                        (this.showEmployeeNumber || this.showExternalId) &&
                                                        (
                                                            <TwoColumns>

                                                                {
                                                                    (this.showEmployeeNumber &&
                                                                        <TextInput required
                                                                                   id="employeeNumber"
                                                                                   label={Localizer.formInputEmployeeNumber}
                                                                                   value={this.user.employeeNumber}
                                                                                   onChange={async (_, item) => await this.onEmployeeNumberChangeAsync(item!)}
                                                                        />
                                                                    )
                                                                }

                                                                {
                                                                    (this.showExternalId &&
                                                                        <TextInput required
                                                                                   id="externalId"
                                                                                   label={Localizer.formInputExternalId}
                                                                                   value={this.user.externalId}
                                                                                   onChange={async (_, item) => await this.onExternalIdChangeAsync(item!)}
                                                                        />
                                                                    )
                                                                }

                                                            </TwoColumns>
                                                        )
                                                    }

                                                    {
                                                        (this.isSubcontractorMounter) &&
                                                        (
                                                            <ThreeColumns>

                                                                <Checkbox id={"canCreateWorkOrderPermissions"}
                                                                          label={Localizer.userManagementMounterPermissionsCanCreateWorkOrder}
                                                                          value={this.user.canCreateWorkOrder}
                                                                          onChange={async (_, value) => await this.onCanCreateWorkOrderChangeAsync(value)}
                                                                />

                                                                <Checkbox id={"canCompleteWorkOrderPermissions"}
                                                                          label={Localizer.userManagementMounterPermissionsCanCompleteWorkOrder}
                                                                          value={this.user.canCompleteWorkOrder}
                                                                          onChange={async (_, value) => await this.onCanCompleteWorkOrderChangeAsync(value)}
                                                                />

                                                                <Checkbox id={"canProcessFormPermissions"}
                                                                          label={Localizer.userManagementMounterPermissionsCanProcessForm}
                                                                          value={this.user.canProcessForm}
                                                                          onChange={async (_, value) => await this.onCanProcessFormChangeAsync(value)}
                                                                />

                                                            </ThreeColumns>
                                                        )
                                                    }

                                                    <TwoColumns>

                                                        <TextInput required
                                                                   id="firstname"
                                                                   label={Localizer.formInputFirstname}
                                                                   value={this.user.firstname}
                                                                   onChange={async (_, item) => await this.onFirstNameChangeAsync(item!)}
                                                        />

                                                        <TextInput required
                                                                   id="lastName"
                                                                   label={Localizer.formInputLastname}
                                                                   value={this.user.lastName}
                                                                   onChange={async (_, item) => await this.onLastNameChangeAsync(item!)}
                                                        />

                                                    </TwoColumns>

                                                    <TwoColumns>

                                                        <TextInput id="middleName"
                                                                   label={Localizer.formInputMiddlename}
                                                                   value={this.user.middleName}
                                                                   onChange={async (_, item) => await this.onMiddleNameChangeAsync(item!)}
                                                        />

                                                        <Dropdown required
                                                                  autoGroupSelected
                                                                  requiredType={DropdownRequiredType.Restricted}
                                                                  id="communicationLanguage"
                                                                  label={Localizer.formInputLanguage}
                                                                  items={EnumProvider.getCommunicationLanguageItems()}
                                                                  selectedItem={EnumProvider.getCommunicationLanguageItem(this.user.communicationLanguage ?? LocalizationHelper.defaultLanguage)}
                                                                  onChange={async (_, item, userInteraction) => await this.onCommunicationLanguageChangeAsync(parseInt(item!.value), userInteraction)}
                                                        />

                                                    </TwoColumns>

                                                    <AddressDivider id="formattedAddress"
                                                                    location={this.user.homeLocation || undefined}
                                                                    onChange={async (_, item) => await this.onAddressChangeAsync(item!)}
                                                    />

                                                    <ThreeColumns>

                                                        <Checkbox id={"constructionSiteCommunicationLanguage"}
                                                                  label={Localizer.userManagementUseConstructionSiteCommunicationLanguage}
                                                                  value={this.user.useConstructionSiteCommunicationLanguage}
                                                                  onChange={async (_, value) => await this.onCommunicationLanguageCheckboxChangeAsync(value)}
                                                        />

                                                        <Checkbox id={"canCommunicateToCustomer"}
                                                                          label={Localizer.userManagementMounterPermissionsCanCommunicateToCustomerForm}
                                                                          value={this.user.canCommunicateToCustomer}
                                                                          onChange={async (_, value) => await this.onCanCommunicateToCustomerFormChangeAsync(value)}
                                                                />

                                                        <FeatureSwitch flagName={FeatureFlags.SendWorkTime}>
                                                            <Checkbox id="sendWorkTime"
                                                                      label={Localizer.userManagementSendWorkTimeLanguageItemName}
                                                                      value={this.user.sendWorkTime}
                                                                      onChange={async (_, value) => await this.onSendWorkTimeChangeAsync(value)}
                                                            />
                                                        </FeatureSwitch>

                                                    </ThreeColumns>

                                                    <ButtonContainer>

                                                        {
                                                            (!this.state.showAddButton) &&
                                                            (
                                                                <Button small
                                                                        id="cancelAddUserButton"
                                                                        minWidth={90}
                                                                        label={Localizer.genericActionCancel}
                                                                        type={ButtonType.Primary}
                                                                        icon={{name: "far ban", size: IconSize.Large}}
                                                                        onClick={async () => await this.cancelAddUserAsync()}
                                                                />
                                                            )
                                                        }

                                                        {
                                                            (this.isModified) &&
                                                            (
                                                                <Button small
                                                                        id="cancelChangesButton"
                                                                        minWidth={90}
                                                                        label={Localizer.genericActionCancel}
                                                                        type={ButtonType.Primary}
                                                                        icon={{name: "far ban", size: IconSize.Large}}
                                                                        confirm={Localizer.userManagementConfirmationButtonRollback}
                                                                        onClick={async () => await this.cancelModifyingAsync()}
                                                                />
                                                            )
                                                        }

                                                        {
                                                            ((this.state.showAddButton) && (!this.user.isDeleted)) &&
                                                            (
                                                                <Button small
                                                                        id="deleteUserButton"
                                                                        minWidth={90}
                                                                        label={Localizer.userManagementButtonDelete}
                                                                        icon={{name: "trash-alt", size: IconSize.Large}}
                                                                        type={ButtonType.Primary}
                                                                        disabled={!this.canDelete || this.isModified || this.state.isPerformingAction}
                                                                        onClick={async () => await this.deleteUserAsync()}
                                                                        confirm={Utility.format(Localizer.userManagementConfirmationButtonDeleteUser, this.userFullName)}
                                                                />
                                                            )
                                                        }

                                                        {
                                                            ((this.state.showAddButton) && (this.user.isDeleted)) &&
                                                            (
                                                                <Button small
                                                                        id="restoreUserButton"
                                                                        minWidth={90}
                                                                        label={Localizer.userManagementButtonRestore}
                                                                        icon={{name: "trash-restore", size: IconSize.Large}}
                                                                        type={ButtonType.Primary}
                                                                        disabled={this.state.isPerformingAction}
                                                                        onClick={async () => await this.restoreUserAsync()}
                                                                        confirm={Utility.format(Localizer.userManagementConfirmationButtonRestoreUser, this.userFullName)}
                                                                />
                                                            )
                                                        }

                                                        {
                                                            ((this.state.showAddButton) && (!this.user.isDeleted) && (this.user.isLocked)) &&
                                                            (
                                                                <Button small
                                                                        id="unlockUserButton"
                                                                        minWidth={90}
                                                                        label={Localizer.userManagementButtonUnlock}
                                                                        icon={{name: "unlock", size: IconSize.Large}}
                                                                        type={ButtonType.Success}
                                                                        disabled={this.state.isPerformingAction}
                                                                        onClick={async () => await this.unlockUserAsync()}
                                                                        confirm={Utility.format(Localizer.userManagementConfirmationButtonUnlockUser, this.userFullName)}
                                                                />
                                                            )
                                                        }

                                                        <Button submit
                                                                id="saveUserButton"
                                                                label={Localizer.formSave}
                                                                type={ButtonType.Orange}
                                                                icon={{name: "far save"}}
                                                                disabled={this.state.isPerformingAction}
                                                        />

                                                    </ButtonContainer>

                                                </Form>

                                            </Tab>

                                            {
                                                (this.state.showAddButton) &&
                                                (

                                                    <Tab id="invitations" title={Localizer.userManagementTabInvitations}>

                                                        <div className={styles.passwordInfo}>
                                                            {
                                                                (!this.user.hasPassword) &&
                                                                (
                                                                    <Alert model={this.alert}/>
                                                                )
                                                            }
                                                        </div>

                                                        <div className={styles.invitations}>

                                                            <Grid id={"invitationsGrid"}
                                                                  columns={this._invitationColumns}
                                                                  data={this.user.invitations}
                                                                  noDataText={Localizer.userManagementGridNoInvitationsText}
                                                            />

                                                        </div>

                                                        <ButtonContainer>

                                                            <Button small
                                                                    minWidth={90}
                                                                    label={Localizer.userManagementButtonResendInvitation}
                                                                    icon={{name: "envelope", size: IconSize.Large}}
                                                                    disabled={this.user.isDeleted || this.user.isLocked || this.user.hasPassword || this.state.isPerformingAction}
                                                                    type={ButtonType.Primary}
                                                                    onClick={async () => await this.resendInvitationAsync()}
                                                                    id="resend"
                                                                    confirm={Utility.format(Localizer.userManagementConfirmationButtonResendInvitation, this.userFullName)}
                                                            />

                                                            {
                                                                (!this.user.sso) &&
                                                                <Button small
                                                                        minWidth={90}
                                                                        label={Localizer.userManagementButtonResetPassword}
                                                                        icon={{name: "repeat", size: IconSize.Large}}
                                                                        disabled={this.user.isDeleted || this.user.isLocked || !this.user.hasPassword || this.state.isPerformingAction}
                                                                        type={ButtonType.Success}
                                                                        onClick={async () => await this.resetPasswordAsync()}
                                                                        confirm={Utility.format(Localizer.userManagementConfirmationButtonResetPassword, this.userFullName)}
                                                                />
                                                            }

                                                        </ButtonContainer>

                                                    </Tab>
                                                )
                                            }

                                            {
                                                (this.isContactPersonRole && UnleashHelper.isEnabled(FeatureFlags.ShowContactPersonExternalIds)) &&
                                                <Tab id="organizationContractMappings" title="External ids">
                                                    <div>
                                                        <table>
                                                            <thead>
                                                                <tr>
                                                                    <th>Contract</th>
                                                                    <th>External id</th>
                                                                </tr>
                                                            </thead>

                                                            <tbody>
                                                                { this.user.roles.map(role =>
                                                                    <tr key={role.organizationContractId + role.externalId}>
                                                                        <td>{role.organizationContract?.name} ({role.organizationContract?.externalId})</td>
                                                                        <td>{role.externalId}</td>
                                                                    </tr>
                                                                )
                                                                }
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </Tab>
                                            }

                                            {
                                                UnleashHelper.isEnabled(FeatureFlags.DefaultWorkOrderValues) &&
                                                <Tab id="defaultWorkOrderValues" title="Default Work Order Values">
                                                    <DefaultWorkOrderValuesTab
                                                        user={this.user}
                                                        defaultValues={this.state.defaultWorkOrderValues}
                                                        workOrderTypes={this.state.workOrderTypes || []}
                                                        constructionSitesOrWarehouses={this.state.constructionSitesOrWarehouses || []}
                                                        managers={this.state.managers || []}
                                                        warehouses={this.state.warehouses || []}
                                                        onSave={(request: SaveUserDefaultWorkOrderValuesRequest) => this.saveDefaultWorkOrderValuesAsync(request)}
                                                        isPerformingAction={this.state.isPerformingAction}
                                                    />
                                                </Tab>
                                            }

                                            {
                                                UnleashHelper.isEnabled(FeatureFlags.AbsenceFields)
                                                && UnleashHelper.isEnabled(FeatureFlags.AbsenceManagement)
                                                && (this.isManager || this.isAdmin || this.isBusinessManager)
                                                && this.user
                                                && !this.user.isContactPerson
                                                &&
                                                <Tab id="absences" title={Localizer.absenceSectionTitle}>
                                                    <AbsencesManagementTab
                                                        user={this.user}
                                                        managers={this.state.managers || []}
                                                        defaultWorkOrderValues={this.state.defaultWorkOrderValues}
                                                        onSave={(request: SaveAbsenceRequest) => this.saveAbsenceAsync(request)}
                                                        onLoadAbsences={(request: GetUserAbsencesRequest) => this.loadUserAbsencesAsync(request)}
                                                        onDelete={(workOrderId: string) => this.deleteAbsenceAsync(workOrderId)}
                                                        isPerformingAction={this.state.isPerformingAction}
                                                    />
                                                </Tab>
                                            }
                                        </TabContainer>
                                    )
                                }

                            </div>

                        </div>
                    </div>
                </PageRow>

                <AddSubcontractorContractModal
                    ref={this._addSubcontractorContractModalRef}
                    addSubcontractorContract={async (sender, request) => await this.addSubcontractorContract(sender, request)}
                />

            </PageContainer>
        );
    }
}
